import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Star, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  ESTATE_DOC_OPTIONS,
  GOAL_OPTIONS,
  formatUSD,
} from "@/lib/familySnapshot";
import { submitSnapshot } from "@/lib/familySnapshot.functions";

export const Route = createFileRoute("/tools/family-snapshot/")({
  head: () => ({
    meta: [
      { title: "The Family Snapshot — Free Planning Tool" },
      {
        name: "description",
        content:
          "A 5-minute, private assessment that gives you a real picture of where your family stands — and what to do next. No login. No sales call.",
      },
      {
        property: "og:title",
        content: "The Family Snapshot — Free Planning Tool",
      },
      {
        property: "og:description",
        content:
          "Five minutes. A real picture of where your family stands financially. No login. No sales call required.",
      },
    ],
    links: [{ rel: "canonical", href: "/tools/family-snapshot" }],
  }),
  component: FamilySnapshotPage,
});

type State = {
  step: number;
  household: {
    your_age: number | "";
    spouse_age: number | "";
    dependents: { age: number }[];
    others_dependent_note: string;
  };
  income: {
    your_income: number | "";
    spouse_income: number | "";
    months_savings: number;
    job_security_rating: number;
  };
  coverage: {
    life_self: number | "";
    life_spouse: number | "";
    has_disability: "yes" | "no" | "unsure" | "";
    has_critical_illness: "yes" | "no" | "unsure" | "";
    estate_docs: string[];
  };
  goals: string[];
  email: string;
  marketing_opt_in: boolean;
};

const STEPS = ["Household", "Income", "Coverage", "Goals", "Results"];

function makeSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function FamilySnapshotPage() {
  const navigate = useNavigate();
  const submit = useServerFn(submitSnapshot);
  const [submitting, setSubmitting] = useState(false);

  const [state, setState] = useState<State>({
    step: 0,
    household: {
      your_age: "",
      spouse_age: "",
      dependents: [],
      others_dependent_note: "",
    },
    income: {
      your_income: "",
      spouse_income: "",
      months_savings: 3,
      job_security_rating: 4,
    },
    coverage: {
      life_self: 0,
      life_spouse: 0,
      has_disability: "",
      has_critical_illness: "",
      estate_docs: [],
    },
    goals: [],
    email: "",
    marketing_opt_in: true,
  });

  const sessionId = useMemo(makeSessionId, []);

  // Per-step validation
  const canAdvance = useMemo(() => {
    switch (state.step) {
      case 0:
        return typeof state.household.your_age === "number" && state.household.your_age >= 18;
      case 1:
        return (
          typeof state.income.your_income === "number" && state.income.your_income > 0
        );
      case 2:
        return (
          state.coverage.has_disability !== "" &&
          state.coverage.has_critical_illness !== ""
        );
      case 3:
        return state.goals.length > 0;
      case 4:
        return /.+@.+\..+/.test(state.email);
      default:
        return false;
    }
  }, [state]);

  const next = () => setState((s) => ({ ...s, step: Math.min(4, s.step + 1) }));
  const back = () => setState((s) => ({ ...s, step: Math.max(0, s.step - 1) }));

  async function handleSubmit() {
    if (!canAdvance) return;
    setSubmitting(true);
    try {
      const payload = {
        session_id: sessionId,
        email: state.email.trim().toLowerCase(),
        marketing_opt_in: state.marketing_opt_in,
        input: {
          household: {
            your_age: Number(state.household.your_age),
            spouse_age:
              typeof state.household.spouse_age === "number"
                ? state.household.spouse_age
                : null,
            dependents: state.household.dependents,
            others_dependent_note: state.household.others_dependent_note,
          },
          income: {
            your_income: Number(state.income.your_income) || 0,
            spouse_income:
              typeof state.income.spouse_income === "number"
                ? state.income.spouse_income
                : null,
            months_savings: state.income.months_savings,
            job_security_rating: state.income.job_security_rating,
          },
          coverage: {
            life_self: Number(state.coverage.life_self) || 0,
            life_spouse: Number(state.coverage.life_spouse) || 0,
            has_disability: state.coverage.has_disability as "yes" | "no" | "unsure",
            has_critical_illness: state.coverage.has_critical_illness as
              | "yes"
              | "no"
              | "unsure",
            estate_docs: state.coverage.estate_docs,
          },
          goals: state.goals,
        },
      };
      const res = await submit({ data: payload });
      if (!res.ok) {
        toast.error(res.error || "Something went wrong");
        setSubmitting(false);
        return;
      }
      navigate({
        to: "/tools/family-snapshot/result/$token",
        params: { token: res.token },
      });
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* HERO */}
        <section className="bg-navy-900 text-cream-50 py-16">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <Eyebrow color="bronze" align="center">Free Tool</Eyebrow>
            <h1
              className="mt-6 font-display font-bold text-cream-50"
              style={{ fontSize: "48px", lineHeight: 1.15, letterSpacing: "-0.01em" }}
            >
              The <em className="text-bronze-400 italic">Family Snapshot.</em>
            </h1>
            <p
              className="mx-auto mt-6 font-sans text-cream-50/85 leading-relaxed"
              style={{ maxWidth: "560px", fontSize: "17px" }}
            >
              5 minutes. A real picture of where your family stands financially —
              and what to do next. No login. No sales call required.
            </p>
            <p className="mt-6 font-sans text-[12px] uppercase tracking-[0.18em] text-bronze-400/90">
              100% private · No sales call required · Used by 500+ families
            </p>
          </div>
        </section>

        {/* ASSESSMENT */}
        <section className="bg-cream-50 py-12">
          <div className="mx-auto px-4" style={{ maxWidth: "720px" }}>
            {/* progress dots */}
            <ol className="mb-8 flex items-center justify-between">
              {STEPS.map((label, idx) => {
                const active = idx === state.step;
                const done = idx < state.step;
                return (
                  <li key={label} className="flex flex-1 flex-col items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold",
                        active
                          ? "border-bronze-600 bg-bronze-600 text-navy-900"
                          : done
                            ? "border-navy-800 bg-navy-800 text-cream-50"
                            : "border-charcoal-500/30 bg-cream-50 text-charcoal-500",
                      )}
                    >
                      {idx + 1}
                    </span>
                    <span
                      className={cn(
                        "hidden sm:block text-[10px] uppercase tracking-[0.18em]",
                        active ? "text-navy-800 font-semibold" : "text-charcoal-500",
                      )}
                    >
                      {label}
                    </span>
                  </li>
                );
              })}
            </ol>

            <div
              className="bg-white border-t-[3px] border-bronze-600 shadow-soft p-8"
              style={{ borderRadius: "4px" }}
            >
              {state.step === 0 && (
                <StepHousehold state={state} setState={setState} />
              )}
              {state.step === 1 && (
                <StepIncome state={state} setState={setState} />
              )}
              {state.step === 2 && (
                <StepCoverage state={state} setState={setState} />
              )}
              {state.step === 3 && (
                <StepGoals state={state} setState={setState} />
              )}
              {state.step === 4 && (
                <StepEmail state={state} setState={setState} />
              )}

              <div className="mt-8 flex items-center justify-between border-t border-bronze-600/30 pt-6">
                <button
                  type="button"
                  onClick={back}
                  disabled={state.step === 0}
                  className="font-sans text-[13px] uppercase tracking-[0.12em] text-charcoal-500 hover:text-navy-800 disabled:opacity-30"
                >
                  ← Back
                </button>
                {state.step < 4 ? (
                  <PrimaryButton
                    type="button"
                    onClick={next}
                    disabled={!canAdvance}
                  >
                    Continue →
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canAdvance || submitting}
                  >
                    {submitting ? "Building your snapshot…" : "Get My Snapshot →"}
                  </PrimaryButton>
                )}
              </div>
            </div>

            <p className="mt-6 text-center font-sans text-[12px] italic text-charcoal-500">
              Projections shown are hypothetical and for educational purposes only.
              They do not represent guaranteed returns or actual policy performance.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/* ─────────────── Step components ─────────────── */

type StepProps = {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
};

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between">
      <Label className="font-sans text-[13px] font-semibold text-navy-800">
        {children}
      </Label>
      {hint && (
        <span className="font-sans text-[11px] text-charcoal-500">{hint}</span>
      )}
    </div>
  );
}

function StepHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-6">
      <h2 className="font-display text-[28px] font-semibold text-navy-800">{title}</h2>
      <p className="mt-1 font-sans text-[14px] text-charcoal-500">{sub}</p>
    </div>
  );
}

function CurrencyInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | "";
  onChange: (n: number | "") => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-sans text-[14px] text-charcoal-500">
        $
      </span>
      <Input
        type="text"
        inputMode="numeric"
        className="pl-7"
        placeholder={placeholder}
        value={value === "" ? "" : value.toLocaleString("en-US")}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^\d]/g, "");
          if (digits === "") onChange("");
          else onChange(Math.min(50_000_000, parseInt(digits, 10)));
        }}
      />
    </div>
  );
}

function StepHousehold({ state, setState }: StepProps) {
  const { household } = state;
  return (
    <div>
      <StepHeader title="Your household" sub="Who are we planning around?" />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <FieldLabel hint="Required">Your age</FieldLabel>
          <Input
            type="number"
            min={18}
            max={110}
            value={household.your_age}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                household: {
                  ...s.household,
                  your_age: e.target.value === "" ? "" : Number(e.target.value),
                },
              }))
            }
          />
        </div>
        <div>
          <FieldLabel hint="Optional">Spouse / partner age</FieldLabel>
          <Input
            type="number"
            min={18}
            max={110}
            value={household.spouse_age}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                household: {
                  ...s.household,
                  spouse_age: e.target.value === "" ? "" : Number(e.target.value),
                },
              }))
            }
          />
        </div>
      </div>

      <div className="mt-6">
        <FieldLabel hint="Optional">Children / dependents</FieldLabel>
        <div className="space-y-2">
          {household.dependents.map((dep, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={40}
                value={dep.age}
                onChange={(e) =>
                  setState((s) => {
                    const deps = [...s.household.dependents];
                    deps[idx] = { age: Number(e.target.value) || 0 };
                    return { ...s, household: { ...s.household, dependents: deps } };
                  })
                }
                placeholder="Age"
                className="max-w-[120px]"
              />
              <button
                type="button"
                onClick={() =>
                  setState((s) => ({
                    ...s,
                    household: {
                      ...s.household,
                      dependents: s.household.dependents.filter((_, i) => i !== idx),
                    },
                  }))
                }
                className="text-charcoal-500 hover:text-danger"
                aria-label="Remove dependent"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setState((s) => ({
                ...s,
                household: {
                  ...s.household,
                  dependents: [...s.household.dependents, { age: 5 }],
                },
              }))
            }
            className="inline-flex items-center gap-1.5 font-sans text-[12px] font-semibold uppercase tracking-[0.12em] text-bronze-600 hover:text-bronze-400"
          >
            <Plus className="h-3.5 w-3.5" /> Add a dependent
          </button>
        </div>
      </div>

      <div className="mt-6">
        <FieldLabel hint="Optional">
          Anyone else who depends on your income? (parents, siblings, others)
        </FieldLabel>
        <Textarea
          rows={2}
          value={household.others_dependent_note}
          maxLength={500}
          onChange={(e) =>
            setState((s) => ({
              ...s,
              household: { ...s.household, others_dependent_note: e.target.value },
            }))
          }
        />
      </div>
    </div>
  );
}

function StepIncome({ state, setState }: StepProps) {
  const { income } = state;
  return (
    <div>
      <StepHeader title="Income & stability" sub="Real numbers — only you see this." />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <FieldLabel hint="Required">Your annual income</FieldLabel>
          <CurrencyInput
            value={income.your_income}
            onChange={(n) =>
              setState((s) => ({ ...s, income: { ...s.income, your_income: n } }))
            }
            placeholder="85,000"
          />
        </div>
        <div>
          <FieldLabel hint="Optional">Spouse / partner income</FieldLabel>
          <CurrencyInput
            value={income.spouse_income}
            onChange={(n) =>
              setState((s) => ({ ...s, income: { ...s.income, spouse_income: n } }))
            }
            placeholder="0"
          />
        </div>
      </div>

      <div className="mt-6">
        <FieldLabel hint={`${income.months_savings >= 12 ? "12+" : income.months_savings} months`}>
          Months of expenses saved
        </FieldLabel>
        <Slider
          value={[income.months_savings]}
          min={0}
          max={12}
          step={1}
          onValueChange={([v]) =>
            setState((s) => ({ ...s, income: { ...s.income, months_savings: v } }))
          }
        />
        <div className="mt-2 flex justify-between font-sans text-[11px] text-charcoal-500">
          <span>0</span>
          <span>6</span>
          <span>12+</span>
        </div>
      </div>

      <div className="mt-6">
        <FieldLabel>How secure is your job / income? </FieldLabel>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() =>
                setState((s) => ({
                  ...s,
                  income: { ...s.income, job_security_rating: n },
                }))
              }
              aria-label={`${n} out of 5`}
              className="p-1 transition-colors"
            >
              <Star
                className={cn(
                  "h-7 w-7",
                  n <= income.job_security_rating
                    ? "fill-bronze-600 text-bronze-600"
                    : "text-charcoal-500/40",
                )}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function YesNoUnsure({
  value,
  onChange,
}: {
  value: "yes" | "no" | "unsure" | "";
  onChange: (v: "yes" | "no" | "unsure") => void;
}) {
  const opts: { v: "yes" | "no" | "unsure"; l: string }[] = [
    { v: "yes", l: "Yes" },
    { v: "no", l: "No" },
    { v: "unsure", l: "Unsure" },
  ];
  return (
    <div className="flex gap-2">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={cn(
            "flex-1 border px-3 py-2 font-sans text-[13px] font-semibold uppercase tracking-[0.08em] transition-colors",
            value === o.v
              ? "border-bronze-600 bg-bronze-600/10 text-navy-800"
              : "border-bronze-600/30 bg-white text-charcoal-500 hover:border-bronze-600 hover:text-navy-800",
          )}
          style={{ borderRadius: "2px" }}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function StepCoverage({ state, setState }: StepProps) {
  const { coverage } = state;
  const hasSpouse =
    typeof state.household.spouse_age === "number" ||
    (typeof state.income.spouse_income === "number" && state.income.spouse_income > 0);
  return (
    <div>
      <StepHeader title="Current coverage" sub="What's in place today?" />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <FieldLabel hint="$ face amount">Life insurance — you</FieldLabel>
          <CurrencyInput
            value={coverage.life_self}
            onChange={(n) =>
              setState((s) => ({ ...s, coverage: { ...s.coverage, life_self: n } }))
            }
            placeholder="0"
          />
        </div>
        {hasSpouse && (
          <div>
            <FieldLabel hint="$ face amount">Life insurance — spouse</FieldLabel>
            <CurrencyInput
              value={coverage.life_spouse}
              onChange={(n) =>
                setState((s) => ({ ...s, coverage: { ...s.coverage, life_spouse: n } }))
              }
              placeholder="0"
            />
          </div>
        )}
      </div>

      <div className="mt-6">
        <FieldLabel hint="Required">Disability income coverage?</FieldLabel>
        <YesNoUnsure
          value={coverage.has_disability}
          onChange={(v) =>
            setState((s) => ({ ...s, coverage: { ...s.coverage, has_disability: v } }))
          }
        />
      </div>

      <div className="mt-6">
        <FieldLabel hint="Required">Critical illness / living benefits?</FieldLabel>
        <YesNoUnsure
          value={coverage.has_critical_illness}
          onChange={(v) =>
            setState((s) => ({
              ...s,
              coverage: { ...s.coverage, has_critical_illness: v },
            }))
          }
        />
      </div>

      <div className="mt-6">
        <FieldLabel hint="Check all that apply">Estate documents in place</FieldLabel>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ESTATE_DOC_OPTIONS.map((opt) => {
            const checked = coverage.estate_docs.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={cn(
                  "flex cursor-pointer items-center gap-3 border px-3 py-2.5 transition-colors",
                  checked
                    ? "border-bronze-600 bg-bronze-600/10"
                    : "border-bronze-600/30 bg-white hover:border-bronze-600",
                )}
                style={{ borderRadius: "2px" }}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) =>
                    setState((s) => {
                      const docs = new Set(s.coverage.estate_docs);
                      if (v) docs.add(opt.value);
                      else docs.delete(opt.value);
                      return {
                        ...s,
                        coverage: { ...s.coverage, estate_docs: Array.from(docs) },
                      };
                    })
                  }
                />
                <span className="font-sans text-[13px] text-navy-800">
                  {opt.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepGoals({ state, setState }: StepProps) {
  return (
    <div>
      <StepHeader
        title="What matters most?"
        sub="Pick everything you'd like your plan to address."
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {GOAL_OPTIONS.map((g) => {
          const active = state.goals.includes(g.value);
          return (
            <button
              key={g.value}
              type="button"
              onClick={() =>
                setState((s) => {
                  const set = new Set(s.goals);
                  if (set.has(g.value)) set.delete(g.value);
                  else set.add(g.value);
                  return { ...s, goals: Array.from(set) };
                })
              }
              className={cn(
                "border px-4 py-3 text-left transition-colors",
                active
                  ? "border-bronze-600 bg-bronze-600/10"
                  : "border-bronze-600/30 bg-white hover:border-bronze-600",
              )}
              style={{ borderRadius: "2px" }}
            >
              <span className="font-sans text-[14px] font-medium text-navy-800">
                {g.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepEmail({ state, setState }: StepProps) {
  const totalIncome =
    (typeof state.income.your_income === "number" ? state.income.your_income : 0) +
    (typeof state.income.spouse_income === "number" ? state.income.spouse_income : 0);
  return (
    <div>
      <StepHeader
        title="Where should we send your snapshot?"
        sub="One email. Your snapshot link inside. Unsubscribe anytime."
      />

      <div className="mb-6 grid grid-cols-2 gap-3">
        <SummaryStat label="Household" value={`${state.household.dependents.length + 1 + (state.household.spouse_age ? 1 : 0)} people`} />
        <SummaryStat label="Combined income" value={formatUSD(totalIncome)} />
        <SummaryStat label="Goals selected" value={`${state.goals.length}`} />
        <SummaryStat label="Estate docs" value={`${state.coverage.estate_docs.length} of 5`} />
      </div>

      <div>
        <FieldLabel hint="Required">Email address</FieldLabel>
        <Input
          type="email"
          value={state.email}
          maxLength={255}
          placeholder="you@example.com"
          onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
        />
      </div>

      <label className="mt-4 flex cursor-pointer items-start gap-3">
        <Checkbox
          checked={state.marketing_opt_in}
          onCheckedChange={(v) =>
            setState((s) => ({ ...s, marketing_opt_in: !!v }))
          }
        />
        <span className="font-sans text-[13px] leading-relaxed text-charcoal-900">
          Also send me one weekly{" "}
          <span className="font-semibold text-navy-800">Family Lesson</span> — short,
          practical, no pitches.
        </span>
      </label>

      <p className="mt-5 font-sans text-[11px] leading-relaxed text-charcoal-500">
        We never sell your information. Your snapshot is for educational purposes
        and is not a substitute for advice from a licensed professional.
      </p>
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-bronze-600/30 px-4 py-3" style={{ borderRadius: "2px" }}>
      <div className="font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
        {label}
      </div>
      <div className="mt-1 font-display text-[18px] font-semibold text-navy-800">
        {value}
      </div>
    </div>
  );
}
